/**
 * AI Policy §8 — the real Customer controls.
 *
 * The published policy promises two things this component implements:
 *
 *   §8 "A Customer administrator can turn AI features off for the organization,
 *      individually or as a group... With them off, no Customer data is sent to
 *      any AI provider."
 *   §3 "Fields a Customer excludes from AI processing in its organization
 *      settings are not sent."
 *
 * Both were promised and neither existed. The only thing that *looked* like a
 * disable was a toggle in AI Assistant settings labelled "Floating AI
 * Assistant", which wrote `localStorage` and disabled nothing — a fake switch
 * next to a legal promise of a real one, which is worse than having neither.
 *
 * **The UI is not the enforcement.** Turning the master switch off writes
 * `settings.ai_enabled = false`, which `services/llm.py` checks on every LLM
 * call — the single chokepoint every AI path in the runtime passes through — and
 * which the control-plane broker re-checks from its own Postgres copy. This
 * component is how an administrator expresses the choice, not what makes it
 * true.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ShieldOff, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** The feature keys §8's "individually" refers to. Mirrors `routes/plans.py`. */
const AI_FEATURES = [
    { key: 'ai_assistant', label: 'AI Assistant', hint: 'Chat, email writing, deal analysis, briefings' },
    { key: 'ai_heartbeat', label: 'AI Heartbeat', hint: 'Scheduled automated analysis runs' },
    { key: 'agentic_personalization', label: 'Agentic personalization', hint: 'AI-drafted outreach held for review' },
    { key: 'floating_ai', label: 'Floating AI entity', hint: 'The always-available assistant surface' },
];

export default function AiPrivacyControls() {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(true);
    const [overrides, setOverrides] = useState({});
    const [catalogue, setCatalogue] = useState([]);
    const [excluded, setExcluded] = useState(new Set());
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [orgRes, catRes] = await Promise.all([
                axios.get(`${API}/organizations/current`, { headers: getAuthHeader() }),
                axios.get(`${API}/ai/field-catalogue`, { headers: getAuthHeader() }),
            ]);
            const settings = orgRes.data?.settings || {};
            setAiEnabled(settings.ai_enabled !== false);
            setOverrides(orgRes.data?.feature_overrides || {});
            setCatalogue(catRes.data?.fields || []);
            setExcluded(new Set(catRes.data?.excluded || []));
        } catch {
            toast.error('Could not load AI privacy settings');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const persist = useCallback(async (patch, successMessage) => {
        setSaving(true);
        try {
            await axios.put(`${API}/organizations/current`, patch, { headers: getAuthHeader() });
            toast.success(successMessage);
            return true;
        } catch (err) {
            // The backend refuses an unqualified token (`title`) rather than
            // guessing which entity it meant — surface that reason rather than
            // a generic failure, because the fix is in the admin's hands.
            const detail = err?.response?.data?.detail;
            toast.error(
                detail?.error === 'invalid_exclusion_token'
                    ? `"${detail.token}" is not a valid field — it must be entity-qualified, like "contact.notes".`
                    : 'Could not save AI privacy settings',
            );
            return false;
        } finally {
            setSaving(false);
        }
    }, [getAuthHeader]);

    const toggleMaster = async () => {
        const next = !aiEnabled;
        setAiEnabled(next);
        const ok = await persist(
            { settings: { ai_enabled: next } },
            next ? 'AI features enabled for this organization'
                 : 'AI features turned off — no data will be sent to any AI provider',
        );
        if (!ok) setAiEnabled(!next);
    };

    const toggleFeature = async (key) => {
        const next = { ...overrides, [key]: overrides[key] === false };
        setOverrides(next);
        if (!(await persist({ feature_overrides: next }, 'Saved'))) setOverrides(overrides);
    };

    const toggleField = async (token) => {
        const next = new Set(excluded);
        next.has(token) ? next.delete(token) : next.add(token);
        setExcluded(next);
        const ok = await persist(
            { settings: { ai_excluded_fields: [...next] } },
            next.has(token) ? 'Field excluded from AI processing' : 'Field re-included',
        );
        if (!ok) setExcluded(excluded);
    };

    const grouped = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const out = {};
        for (const f of catalogue) {
            if (q && !f.label.toLowerCase().includes(q) && !f.token.toLowerCase().includes(q)) continue;
            (out[f.entity] ||= []).push(f);
        }
        return out;
    }, [catalogue, filter]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-8 text-sm text-stone-500" aria-busy="true">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading AI privacy settings…
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ─── Master switch (§8 "as a group") ─────────────────────────── */}
            <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                        {aiEnabled
                            ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            : <ShieldOff className="w-5 h-5 text-stone-500 shrink-0 mt-0.5" />}
                        <div>
                            <h3 className="text-sm font-semibold text-stone-900">AI features</h3>
                            <p className="text-xs text-stone-500 mt-0.5">
                                {aiEnabled
                                    ? 'On. Only the records relevant to a request are sent — never your whole database.'
                                    : 'Off. No data from this organization is sent to any AI provider.'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={toggleMaster}
                        disabled={saving}
                        role="switch"
                        aria-checked={aiEnabled}
                        aria-label="AI features for this organization"
                        data-testid="ai-master-toggle"
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${aiEnabled ? 'bg-violet-600' : 'bg-stone-300'}`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${aiEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                </div>
            </div>

            {/* ─── Per-feature (§8 "individually") ─────────────────────────── */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-1">Individual features</h3>
                <p className="text-xs text-stone-500 mb-3">
                    Turn single features off while leaving the rest on. Features not
                    included in your plan stay unavailable regardless.
                </p>
                <div className="space-y-2">
                    {AI_FEATURES.map(f => {
                        const off = overrides[f.key] === false;
                        return (
                            <div key={f.key} className="flex items-center justify-between gap-4 p-3 rounded-lg border border-stone-200">
                                <div>
                                    <p className="text-sm text-stone-900">{f.label}</p>
                                    <p className="text-xs text-stone-500">{f.hint}</p>
                                </div>
                                <button
                                    onClick={() => toggleFeature(f.key)}
                                    disabled={saving || !aiEnabled}
                                    role="switch"
                                    aria-checked={!off}
                                    aria-label={f.label}
                                    data-testid={`ai-feature-${f.key}`}
                                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${!off && aiEnabled ? 'bg-violet-600' : 'bg-stone-300'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${!off && aiEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ─── Field exclusions (§3) ───────────────────────────────────── */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-1">Excluded fields</h3>
                <p className="text-xs text-stone-500 mb-3">
                    Excluded fields are removed from the record <em>before</em> anything is
                    sent — they are not blanked out, they are absent. Custom fields your
                    organization defined appear alongside the standard ones.
                </p>

                <div className="relative mb-3">
                    <Search className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder="Search fields…"
                        aria-label="Search fields"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-200"
                    />
                </div>

                <div className="space-y-4">
                    {Object.entries(grouped).map(([entity, fields]) => (
                        <div key={entity}>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 mb-1.5">{entity}</p>
                            <div className="grid sm:grid-cols-2 gap-1.5">
                                {fields.map(f => (
                                    <label
                                        key={f.token}
                                        className="flex items-center gap-2 p-2 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={excluded.has(f.token)}
                                            onChange={() => toggleField(f.token)}
                                            disabled={saving}
                                            data-testid={`ai-exclude-${f.token}`}
                                            className="rounded border-stone-300 text-violet-600 focus:ring-violet-500"
                                        />
                                        <span className="text-sm text-stone-700">{f.label}</span>
                                        {f.source === 'custom' && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">custom</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                    {Object.keys(grouped).length === 0 && (
                        <p className="text-sm text-stone-400">No fields match “{filter}”.</p>
                    )}
                </div>
            </div>

            <p className="text-xs text-stone-400">
                These controls implement §3 and §8 of the{' '}
                <a href="/ai-policy" className="underline hover:text-stone-600">AI Policy</a>.
            </p>
        </div>
    );
}
